import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { EASE, Kinetic, Reveal } from "@/components/motion";
import { ARTICLES, articleBySlug, useTitle } from "@/data/site";

function Block({ block }) {
  if (block.type === "h2") {
    return (
      <Reveal>
        <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink sm:text-4xl">
          {block.text}
        </h2>
      </Reveal>
    );
  }
  if (block.type === "img") {
    return (
      <Reveal>
        <figure className="my-4">
          <div className="overflow-hidden">
            <img src={block.src} alt={block.caption || ""} loading="lazy" className="w-full object-cover" />
          </div>
          {block.caption && (
            <figcaption className="mt-4 text-xs uppercase tracking-[0.22em] text-steel">
              {block.caption}
            </figcaption>
          )}
        </figure>
      </Reveal>
    );
  }
  if (block.type === "quote") {
    return (
      <Reveal>
        <blockquote className="border-l-2 border-gold py-2 pl-8 font-serif text-3xl font-light italic leading-snug tracking-tight text-ink sm:text-4xl">
          {block.text}
        </blockquote>
      </Reveal>
    );
  }
  return (
    <Reveal>
      <p className="text-lg leading-[1.9] text-graphite">{block.text}</p>
    </Reveal>
  );
}

export default function Article() {
  const { slug } = useParams();
  const article = articleBySlug(slug);
  useTitle(article ? `${article.title} | Urban Trail Journal` : "Journal | Urban Trail");

  if (!article) return <Navigate to="/journal" replace />;

  const related = ARTICLES.filter((a) => a.slug !== slug);

  return (
    <div data-testid="article-page">
      <section className="relative flex h-[78vh] min-h-[540px] w-full items-end overflow-hidden bg-ink">
        <motion.img
          src={article.hero}
          alt={article.title}
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-16 lg:px-12 lg:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          >
            <Link to="/journal" data-testid="article-back" className="label text-white/70 transition-colors hover:text-gold">
              ← Journal
            </Link>
          </motion.div>
          <motion.p
            className="label mt-8 text-gold"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
          >
            {article.category}
          </motion.p>
          <h1 className="mt-5 max-w-4xl font-serif text-4xl font-light leading-[1.0] tracking-tight text-white sm:text-6xl lg:text-7xl">
            <Kinetic lines={[article.title]} delay={0.35} />
          </h1>
          <motion.p
            className="mt-6 text-xs uppercase tracking-[0.24em] text-white/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.8 }}
          >
            {article.author} · {article.date}
          </motion.p>
        </div>
      </section>

      <article className="bg-white">
        <div className="mx-auto max-w-3xl space-y-12 px-6 py-24 lg:py-36">
          {article.body.map((b, i) => (
            <Block key={i} block={b} />
          ))}
        </div>
      </article>

      <section className="border-t border-mist bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-24 lg:px-12 lg:py-32">
          <Reveal>
            <p className="label text-gold">Continue Reading</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-serif text-4xl font-light tracking-tight text-ink">Related stories</h2>
          </Reveal>
          <div className="mt-14 grid gap-x-10 gap-y-16 sm:grid-cols-2">
            {related.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.1}>
                <Link to={`/journal/${a.slug}`} data-testid={`related-${a.slug}`} className="group block">
                  <div className="overflow-hidden">
                    <img
                      src={a.hero}
                      alt={a.title}
                      loading="lazy"
                      className="aspect-[16/10] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <p className="label mt-6 text-gold">{a.category}</p>
                  <h3 className="mt-3 font-serif text-2xl font-light leading-snug tracking-tight text-ink sm:text-3xl">
                    {a.title}
                  </h3>
                  <span className="link-arrow mt-4 text-ink transition-colors group-hover:text-gold">
                    Read Story <span aria-hidden>→</span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
